import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
    Card, 
    Button, 
    Rate, 
    Tag, 
    Space, 
    Typography, 
    Row, 
    Col, 
    Divider, 
    Avatar, 
    Badge, 
    Tabs, 
    Image, 
    Skeleton,
    Alert,
    Statistic,
    Progress,
    Tooltip,
    Modal,
    Carousel
} from 'antd';
import MenuCard from '../../UI/MenuCard';
import { formatCurrency, getStatusColor } from '../../../utils/ui';

// Food decoration styles
const foodDecorationStyles = `
    .floating-food {
        position: absolute;
        opacity: 0.08;
        animation: float 8s ease-in-out infinite;
        pointer-events: none;
        z-index: 1;
    }
    
    .floating-food:nth-child(odd) {
        animation-delay: -3s;
        animation-duration: 10s;
    }
    
    .floating-food:nth-child(even) {
        animation-delay: -6s;
        animation-duration: 12s;
    }
    
    @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        25% { transform: translateY(-15px) rotate(3deg); }
        50% { transform: translateY(-8px) rotate(-2deg); }
        75% { transform: translateY(-12px) rotate(1deg); }
    }
    
    .food-bounce {
        animation: bounce 4s ease-in-out infinite;
    }
    
    @keyframes bounce {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-8px); }
    }
    
    .food-spin {
        animation: spin 25s linear infinite;
    }
    
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
    
    .food-pulse {
        animation: pulse 3s ease-in-out infinite;
    }
    
    @keyframes pulse {
        0%, 100% { opacity: 0.08; transform: scale(1); }
        50% { opacity: 0.15; transform: scale(1.05); }
    }
`;

// Food decoration component
const FoodDecoration = ({ emoji, className = "", style = {} }) => (
    <div className={`floating-food ${className}`} style={style}>
        <span className="text-3xl">{emoji}</span>
    </div>
);

import { 
    StarOutlined, 
    EnvironmentOutlined, 
    PhoneOutlined, 
    MailOutlined, 
    ClockCircleOutlined, 
    UserOutlined,
    HeartOutlined,
    ShareAltOutlined,
    ShoppingCartOutlined,
    CheckCircleOutlined,
    TrophyOutlined,
    FireOutlined,
    CrownOutlined,
    SafetyOutlined,
    ThunderboltOutlined,
    EyeOutlined,
    MessageOutlined,
    CalendarOutlined,
    DollarOutlined,
    TeamOutlined,
    CarOutlined,
    GiftOutlined
} from '@ant-design/icons';
import api from '../../../services/api';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

function VendorShow() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [vendor, setVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [favorites, setFavorites] = useState(new Set());
    const [selectedMenu, setSelectedMenu] = useState(null);
    const [menuModalVisible, setMenuModalVisible] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.getVendor(id);
                setVendor(res.data);
            } catch (e) {
                setError('Failed to load vendor');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    const toggleFavorite = (vendorId) => {
        setFavorites(prev => {
            const newFavorites = new Set(prev);
            if (newFavorites.has(vendorId)) {
                newFavorites.delete(vendorId);
            } else {
                newFavorites.add(vendorId);
            }
            return newFavorites;
        });
    };

    const handleBookNow = (menu = null) => {
        const params = new URLSearchParams({
            vendor_id: vendor.id,
            booking_type: menu ? 'menu' : 'general',
        });
        if (menu) {
            params.append('menu_id', menu.id);
        }
        navigate(`/booking?${params.toString()}`);
    };

    const handleViewMenu = (menu) => {
        setSelectedMenu(menu);
        setMenuModalVisible(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <Skeleton active paragraph={{ rows: 8 }} />
                </div>
            </div>
        );
    }

    if (error || !vendor) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                <div className="max-w-md mx-auto text-center">
                    <Alert
                        message="Vendor Not Found"
                        description={error || 'The vendor you are looking for does not exist.'}
                        type="error"
                        showIcon
                        action={
                            <Button size="small" onClick={() => navigate('/')}>
                                Go Home
                            </Button>
                        }
                    />
                </div>
            </div>
        );
    }

    const isFavorite = favorites.has(vendor.id);
    const rating = Number(vendor.rating) || 0;
    const reviewCount = Number(vendor.total_reviews) || 0;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 relative overflow-hidden">
            <style>{foodDecorationStyles}</style>
            {/* Food Decorations */}
            <FoodDecoration emoji="🍽️" className="food-bounce" style={{ top: '5%', left: '3%' }} />
            <FoodDecoration emoji="🥘" className="food-spin" style={{ top: '15%', right: '5%' }} />
            <FoodDecoration emoji="🍜" className="food-pulse" style={{ top: '25%', left: '8%' }} />
            <FoodDecoration emoji="🍱" className="food-bounce" style={{ top: '35%', right: '10%' }} />
            <FoodDecoration emoji="🍲" className="food-spin" style={{ top: '45%', left: '2%' }} />
            <FoodDecoration emoji="🍳" className="food-pulse" style={{ top: '55%', right: '3%' }} />
            <FoodDecoration emoji="🥗" className="food-bounce" style={{ top: '65%', left: '6%' }} />
            <FoodDecoration emoji="🍤" className="food-spin" style={{ top: '75%', right: '8%' }} />
            <FoodDecoration emoji="🍛" className="food-pulse" style={{ top: '85%', left: '4%' }} />
            <FoodDecoration emoji="🍝" className="food-bounce" style={{ top: '95%', right: '6%' }} />
            
            {/* Hero Section */}
            <div className="relative">
                {/* Background Image/Pattern */}
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 opacity-90"></div>
                <div className="absolute inset-0 bg-black opacity-20"></div>
                
                {/* Hero Content */}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                    <Row gutter={[24, 24]} align="middle">
                        <Col xs={24} lg={16}>
                            <div className="text-white">
                                <div className="flex items-center gap-3 mb-3 sm:mb-4">
                                    <Tag color={getStatusColor(vendor.is_verified ? 'success' : 'pending')}>
                                        {vendor.is_verified ? 'Verified' : 'Pending'}
                                    </Tag>
                                    <Tag color="gold" icon={<CrownOutlined />}>
                                        Premium Partner
                                    </Tag>
                                </div>
                                
                                <Title level={1} className="!text-white !mb-3 sm:!mb-4 !text-3xl sm:!text-5xl font-bold">
                                    {vendor.business_name}
                                </Title>
                                
                                <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-5 sm:mb-6">
                                    <div className="flex items-center gap-2">
                                        <Rate disabled value={rating} className="text-yellow-400" />
                                        <Text className="text-white text-base sm:text-lg font-semibold">
                                            {rating.toFixed(1)} ({reviewCount} reviews)
                                        </Text>
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                        <EnvironmentOutlined className="text-white text-lg" />
                                        <Text className="text-white text-base sm:text-lg">{vendor.city?.name}</Text>
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                        <DollarOutlined className="text-white text-lg" />
                                        <Text className="text-white text-base sm:text-lg">Min: {formatCurrency(vendor.min_order_amount)}</Text>
                                    </div>
                                </div>
                                
                                <Paragraph className="!text-white !text-base sm:!text-lg !mb-6 sm:!mb-8 max-w-3xl">
                                    {vendor.description}
                                </Paragraph>
                                
                                <Space size="middle" wrap>
                                    <Button 
                                        type="primary" 
                                        size="large" 
                                        icon={<CalendarOutlined />}
                                        onClick={() => handleBookNow()}
                                        className="h-11 sm:h-12 px-6 sm:px-8 rounded-xl font-semibold text-base sm:text-lg bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl transition-all duration-300"
                                    >
                                        Book Now
                                    </Button>
                                    
                                    <Button 
                                        size="large" 
                                        icon={<HeartOutlined />}
                                        onClick={() => toggleFavorite(vendor.id)}
                                        className={`h-11 sm:h-12 px-5 sm:px-6 rounded-xl font-semibold text-base sm:text-lg border-2 transition-all duration-300 ${
                                            isFavorite 
                                                ? 'bg-red-500 border-red-500 text-white hover:bg-red-600' 
                                                : 'bg-white/20 border-white/30 text-white hover:bg-white/30'
                                        }`}
                                    >
                                        {isFavorite ? 'Saved' : 'Save'}
                                    </Button>
                                    
                                    <Button 
                                        size="large" 
                                        icon={<ShareAltOutlined />}
                                        className="h-11 sm:h-12 px-5 sm:px-6 rounded-xl font-semibold text-base sm:text-lg bg-white/20 border-white/30 text-white hover:bg-white/30"
                                    >
                                        Share
                                    </Button>
                                </Space>
                            </div>
                        </Col>
                        
                        <Col xs={24} lg={8}>
                            <div className="text-center">
                                <div className="relative inline-block">
                                    <Avatar 
                                        size={200} 
                                        icon={<UserOutlined />}
                                        className="bg-white/20 border-4 border-white/30 shadow-2xl"
                                    />
                                    <div className="absolute -bottom-2 -right-2 bg-green-500 text-white rounded-full p-2 shadow-lg">
                                        <CheckCircleOutlined className="text-xl" />
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
                <Row gutter={[24, 24]}>
                    {/* Left Column - Main Content */}
                    <Col xs={24} lg={16}>
                        <Tabs defaultActiveKey="menus" size="large">
                            <TabPane 
                                tab={
                                    <span className="flex items-center gap-2">
                                        <ShoppingCartOutlined />
                                        Menus & Packages
                                    </span>
                                } 
                                key="menus"
                            >
                                <div className="space-y-5 sm:space-y-6">
                                    <div className="flex items-center justify-between">
                                        <Title level={3} className="!mb-0">Our Menu Collection</Title>
                                        <Text type="secondary">{vendor.menus?.length || 0} items available</Text>
                                    </div>
                                    
                                    <Row gutter={[12, 12]}>
                                        {vendor.menus?.map((menu) => (
                                            <Col xs={24} sm={12} lg={8} key={menu.id}>
                                                <MenuCard
                                                    item={menu}
                                                    onView={() => handleViewMenu(menu)}
                                                    onAddToCart={() => handleBookNow(menu)}
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            </TabPane>
                            
                            <TabPane 
                                tab={
                                    <span className="flex items-center gap-2">
                                        <MessageOutlined />
                                        Reviews & Ratings
                                    </span>
                                } 
                                key="reviews"
                            >
                                <div className="space-y-5 sm:space-y-6">
                                    <div className="text-center py-8">
                                        <div className="text-6xl mb-4">⭐</div>
                                        <Title level={2} className="!mb-2">{rating.toFixed(1)}</Title>
                                        <Rate disabled value={rating} className="text-2xl mb-2" />
                                        <Text type="secondary" className="text-lg">Based on {reviewCount} reviews</Text>
                                    </div>
                                    
                                    <div className="bg-white rounded-xl p-6 shadow-sm">
                                        <Title level={4} className="!mb-4">Customer Reviews</Title>
                                        <div className="space-y-4">
                                            {/* Sample reviews - in real app, these would come from API */}
                                            {[1, 2, 3].map((i) => (
                                                <div key={i} className="border-b border-gray-100 pb-4 last:border-b-0">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <Avatar icon={<UserOutlined />} />
                                                        <div>
                                                            <Text strong>Customer {i}</Text>
                                                            <div className="flex items-center gap-2">
                                                                <Rate disabled value={5} size="small" />
                                                                <Text type="secondary">2 days ago</Text>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Paragraph className="!mb-0">
                                                        Excellent service and delicious food! Highly recommended for any event.
                                                    </Paragraph>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </TabPane>
                            
                            <TabPane 
                                tab={
                                    <span className="flex items-center gap-2">
                                        <SafetyOutlined />
                                        About & Policies
                                    </span>
                                } 
                                key="about"
                            >
                                <div className="space-y-5 sm:space-y-6">
                                    <Card>
                                        <Title level={4} className="!mb-4">About {vendor.business_name}</Title>
                                        <Paragraph className="!mb-4">
                                            {vendor.description}
                                        </Paragraph>
                                        <Divider />
                                        <Row gutter={[12, 12]}>
                                            <Col xs={24} sm={12}>
                                                <div className="flex items-center gap-3 mb-3">
                                                    <ClockCircleOutlined className="text-orange-500 text-xl" />
                                                    <div>
                                                        <Text strong>Operating Hours</Text>
                                                        <br />
                                                        <Text type="secondary">Mon-Sun: 9:00 AM - 10:00 PM</Text>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <div className="flex items-center gap-3 mb-3">
                                                    <CarOutlined className="text-orange-500 text-xl" />
                                                    <div>
                                                        <Text strong>Delivery</Text>
                                                        <br />
                                                        <Text type="secondary">Free delivery within 5km</Text>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <div className="flex items-center gap-3 mb-3">
                                                    <TeamOutlined className="text-orange-500 text-xl" />
                                                    <div>
                                                        <Text strong>Minimum Order</Text>
                                                        <br />
                                                        <Text type="secondary">₹{vendor.min_order_amount}</Text>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <div className="flex items-center gap-3 mb-3">
                                                    <GiftOutlined className="text-orange-500 text-xl" />
                                                    <div>
                                                        <Text strong>Special Offers</Text>
                                                        <br />
                                                        <Text type="secondary">10% off on orders above ₹5000</Text>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card>
                                </div>
                            </TabPane>
                        </Tabs>
                    </Col>
                    
                    {/* Right Column - Sidebar */}
                    <Col xs={24} lg={8}>
                        <div className="space-y-5 sm:space-y-6">
                            {/* Quick Stats */}
                            <Card title="Quick Stats" className="shadow-sm">
                                <Row gutter={[12, 12]}>
                                    <Col span={12}>
                                        <Statistic 
                                            title="Rating" 
                                            value={rating} 
                                            precision={1}
                                            prefix={<StarOutlined className="text-yellow-400" />}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic 
                                            title="Reviews" 
                                            value={reviewCount}
                                            prefix={<MessageOutlined />}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic 
                                            title="Min Order" 
                                            value={formatCurrency(vendor.min_order_amount)}
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic 
                                            title="Experience" 
                                            value="5+"
                                            suffix="Years"
                                            prefix={<TrophyOutlined />}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                            
                            {/* Contact Info */}
                            <Card title="Contact Information" className="shadow-sm">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <PhoneOutlined className="text-orange-500 text-xl" />
                                        <div>
                                            <Text strong>Phone</Text>
                                            <br />
                                            <Text type="secondary">+91 7483423925</Text>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <MailOutlined className="text-orange-500 text-xl" />
                                        <div>
                                            <Text strong>Email</Text>
                                            <br />
                                            <Text type="secondary">{vendor.email}</Text>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <EnvironmentOutlined className="text-orange-500 text-xl" />
                                        <div>
                                            <Text strong>Location</Text>
                                            <br />
                                            <Text type="secondary">{vendor.city?.name}</Text>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                            
                            {/* Special Features */}
                            <Card title="Why Choose Us?" className="shadow-sm">
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <CheckCircleOutlined className="text-green-500" />
                                        <Text>100% Vegetarian & Non-Vegetarian</Text>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <CheckCircleOutlined className="text-green-500" />
                                        <Text>Fresh Ingredients Daily</Text>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <CheckCircleOutlined className="text-green-500" />
                                        <Text>Professional Service Staff</Text>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <CheckCircleOutlined className="text-green-500" />
                                        <Text>Customizable Menu Options</Text>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <CheckCircleOutlined className="text-green-500" />
                                        <Text>On-time Delivery Guarantee</Text>
                                    </div>
                                </div>
                            </Card>
                            
                            {/* CTA Card */}
                            <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-none">
                                <div className="text-center">
                                    <Title level={4} className="!text-white !mb-2">Ready to Book?</Title>
                                    <Paragraph className="!text-white/90 !mb-4">
                                        Get started with your catering needs today!
                                    </Paragraph>
                                    <Button 
                                        type="primary" 
                                        size="large" 
                                        onClick={() => handleBookNow()}
                                        className="bg-white text-orange-600 border-white hover:bg-gray-100 hover:text-orange-700 h-11 sm:h-12 px-6 sm:px-8 rounded-xl font-semibold"
                                    >
                                        Book Now
                                    </Button>
                                </div>
                            </Card>
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Menu Details Modal */}
            <Modal
                title={selectedMenu?.name}
                open={menuModalVisible}
                onCancel={() => setMenuModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setMenuModalVisible(false)}>
                        Close
                    </Button>,
                    <Button 
                        key="book" 
                        type="primary" 
                        onClick={() => {
                            setMenuModalVisible(false);
                            handleBookNow(selectedMenu);
                        }}
                    >
                        Book This Menu
                    </Button>
                ]}
                width={600}
            >
                {selectedMenu && (
                    <div className="space-y-4">
                        <div className="text-center">
                            <div className="h-32 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg flex items-center justify-center mb-4">
                                <span className="text-4xl">🍽️</span>
                            </div>
                            <Title level={3} className="!mb-2">{selectedMenu.name}</Title>
                        </div>
                        
                        <Divider />
                        
                        <div>
                            <Title level={5}>Description</Title>
                            <Paragraph>{selectedMenu.description}</Paragraph>
                        </div>
                        
                        <div>
                            <Title level={5}>What's Included</Title>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Fresh ingredients</li>
                                <li>Professional presentation</li>
                                <li>On-time delivery</li>
                                <li>Setup assistance</li>
                            </ul>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default VendorShow;